import Color from 'color';
import { ColorScheme } from '../schema/ColorScheme';

const DEFAULT_MINIMUM_CONTRAST = 4.5;
const TEXT_CONTRAST_WHITE_PREFERENCE_BUFFER = 0.3;

/**
 * Cache of optimal text colors for the given combination
 */
const optimalColorCache: Record<string, string> = {};

function createCacheKey(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number
): string {
    return (
        backgroundColor +
        '_' +
        colorScheme.schemePrimary +
        '_' +
        desiredColor +
        '_' +
        minimumContrast
    );
}

/**
 * Calculates the optimal text color on the given background from the given color scheme, defaulting to the desired color if it
 * passes the minimum contrast checks. Caches the values calculated for future calculation
 * @param backgroundColor the background color
 * @param colorScheme the color scheme
 * @param desiredColor the desired color to use, will be default if accessible
 * @param minimumContrast the contrast to use, defaults to 4.5
 */
export function getOptimalTextColor(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColor: string,
    minimumContrast: number = DEFAULT_MINIMUM_CONTRAST
): string {
    const cacheKey = createCacheKey(backgroundColor, colorScheme, desiredColor, minimumContrast);
    let optimalColor = optimalColorCache[cacheKey];

    if (!optimalColor) {
        optimalColor = getOptimalTextColorString(
            backgroundColor,
            colorScheme,
            desiredColor,
            minimumContrast
        );
        optimalColorCache[cacheKey] = optimalColor;
    }

    return optimalColor;
}

/**
 * Calculates optimal text color from the colorScheme for the given background color
 *
 * Given a background color and an array of possible colors to use for text on it, we find the closest of the given colors + white/black
 * to the minimum contrast that still exceeds the minimum contrast. If the best color is black, returns a color with the same Hue and Saturation as
 * schemeDarker, and a brightness Value set to as bright as possible while still passing minimum contrast
 *
 * @param backgroundColor the background color for the text to go on
 * @param colorArray the array of colors to search for a possible value. Will also try black and white
 * @param desiredColorString the color to use assuming it passes the contrast check
 * @param minimumContrast the minimum contrast the text must pass
 */
function getOptimalTextColorString(
    backgroundColor: string,
    colorScheme: ColorScheme,
    desiredColorString: string,
    minimumContrast: number = DEFAULT_MINIMUM_CONTRAST
): string {
    // first see if desired color passes, if so we can just return that immediately
    const background = Color(backgroundColor);
    const desiredColor = Color(desiredColorString);
    if (desiredColor.contrast(background) >= minimumContrast) {
        return desiredColor.hex();
    }

    // if desired color doesn't pass, look through all the colors in the colorScheme, and find the one closest to
    // the minimum contrast that still passes, if any
    let closestContrast = 21; // contrast between black and white, largest value for a contrast
    let bestColor: Color;
    // also keep track of the contrast and color that come closest to satisfying the minimum contrast, in case we are never able to find a value that passes
    let fallbackContrast = 0;
    let fallbackColor: Color;

    Object.keys(colorScheme).forEach(key => {
        const colorString = colorScheme[key];
        if (colorString && typeof colorString === 'string') {
            const color = Color(colorString);
            const contrast = color.contrast(background);
            if (contrast >= minimumContrast && contrast < closestContrast) {
                bestColor = color;
                closestContrast = contrast;
                // keep track of most optimal fallback color/contrast
            } else if (contrast < minimumContrast && contrast > fallbackContrast) {
                fallbackColor = color;
                fallbackContrast = contrast;
            }
        }
    });

    if (bestColor) {
        return bestColor.hex();
    }

    // if we still haven't found a color good enough, we need to check black and white (adding both black and white guarantees we'll pass)
    // check white first over black
    const whiteColor = Color({ r: 255, g: 255, b: 255 });
    const whiteContrast = whiteColor.contrast(background);
    if (whiteContrast >= minimumContrast) {
        return whiteColor.hex();
        // continue tracking most optimal fallback color/contrast
    } else if (whiteContrast < minimumContrast && whiteContrast > fallbackContrast) {
        fallbackColor = whiteColor;
        fallbackContrast = whiteContrast;
    }

    // finally, we're forced to use black. But we'd ideally like at least a shade of the color that's the same as primary, so rather than just use black
    // we'll search for a value that's dark enough to pass contrast, but still the same hue and saturation as the primary value
    // so we'll start from schemePrimary and binary search between schemeDarker and black, adjusting the V value of the HSV,
    // to find the lightest color of the same H and S value that still passes minimumContrast
    let currentColor = Color(colorScheme.schemePrimary);

    let lowerVBound = 0;
    let upperVBound = currentColor.value();

    // we take 7 steps because binary searching for a V value between 0 and 100, 2^7 = 128, so we're sure we'll hit a good value
    for (let steps = 0; steps < 7; steps++) {
        const currentV = (upperVBound + lowerVBound) / 2;

        currentColor = currentColor.value(currentV);
        const newContrast = currentColor.contrast(background);

        // continue tracking most optimal fallback color/contrast
        if (newContrast < minimumContrast && newContrast > fallbackContrast) {
            fallbackColor = currentColor;
            fallbackContrast = newContrast;
        }

        if (newContrast < minimumContrast + 0.1) {
            upperVBound = currentV;
        } else {
            lowerVBound = currentV;
        }
    }

    // choose the color that passes the minimum contrast, or else comes closest to satisfying the minimum contrast
    let currentContrast = currentColor.contrast(background);
    if (currentContrast < minimumContrast && fallbackColor && currentContrast < fallbackContrast) {
        currentColor = fallbackColor;
        currentContrast = fallbackContrast;
    }

    if (
        whiteContrast >= DEFAULT_MINIMUM_CONTRAST &&
        Math.abs(whiteContrast - currentContrast) < TEXT_CONTRAST_WHITE_PREFERENCE_BUFFER
    ) {
        // in some scenarios, the requested minimum contrast is > default minimum.
        // here, white and black might both have failed, and black might have higher contrast with the background color than white.
        // however, per request from design, even if color contrast is technically better with black, prefer white if it at least passes
        // the default minimum in those cases, and the difference is smaller than some delta
        return whiteColor.hex();
    }

    return currentColor.hex();
}
